import { isSystemPipelineId } from 'app/components/action-center/systemPipelines'
import { logWarn } from 'lib/logging'

/** One node, in either the canvas shape or the engine's stored shape. */
type PipelineWritingNode = {
  type?: string | null
  pipelineId?: string | null
  data?: { pipelineId?: string | null } | null
}

/**
 * Whether a workflow writes a membership on a system pipeline, which makes it
 * the Action Center's rather than the practice's.
 *
 * Read off the nodes rather than a stored flag, because a flag can disagree
 * with the workflow it is attached to once somebody edits the nodes.
 *
 * `nodesJson` is typed as an object by codegen but arrives as a JSON string, so
 * both are accepted. Unparseable reads as ours: wrong that way hides a workflow
 * a practice could have seen, wrong the other way hands them one whose edits
 * would silently change their own queues.
 */
export function isSystemEnrolmentWorkflow(workflow: { nodesJson?: unknown } | null | undefined): boolean {
  const raw = workflow?.nodesJson
  if (!raw) return false

  if (typeof raw !== `string`) return isSystemEnrolmentNodes(raw)
  try {
    return isSystemEnrolmentNodes(JSON.parse(raw))
  } catch (err) {
    logWarn(`workflows`, `Could not read a workflow's nodes; treating it as ours`, err)
    return true
  }
}

/**
 * The same question asked of nodes already in hand: a local draft on the canvas,
 * or the nodes a template builds. Saves a caller having to dress an array up as
 * the server's `nodesJson` to ask it.
 */
export function isSystemEnrolmentNodes(nodes: unknown): boolean {
  return (
    Array.isArray(nodes) &&
    (nodes as PipelineWritingNode[]).some(
      (node) => isSystemPipelineId(node?.pipelineId) || isSystemPipelineId(node?.data?.pipelineId),
    )
  )
}
